import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { reaction } from "mobx";
import ScriptWaveformDisplay from "./ScriptWaveformDisplay";
import type { ScriptLayerScene, ScriptLayerSegment, TransitionConfig } from "../../../engine/clips/script/script.interfaces";
import { DEFAULT_SCENE_TRANSITION } from "../../../engine/clips/script/script.interfaces";
import Clip from "../../../librettoEditor/modules/timeline/components/Clip/Clip";
import { TransitionHandle, TransitionConfig as UITransitionConfig, VisualTransitionConfig } from "../../../librettoEditor/modules/timeline/components/TransitionHandle";
import { CompositionStateStore } from "../../../librettoEditor/store/CompositionStateStore";
import { ScriptLayerStore } from "../../../librettoEditor/store/ScriptLayerStore";
import { useAssetStore } from "../../../storage/AssetStore";
import { CompositionService } from "../../../librettoEditor/services/CompositionService";
import { ProjectAssetsStore } from "../../../state/ProjectAssetsStore";
import { ApplicationStore } from "../../../librettoEditor/store/ApplicationStore";
import { CompositionEventsEnum } from "../../../librettoEditor/CompositionEvents";
import { WaveformProgressStore } from "../../../state/WaveformProgressStore";
import { getScriptClipAudioEntriesSignature } from "../../../librettoEditor/utils/scriptTranscriptWords";
import ScriptClipSectionSelection, {
  resolveScriptClipSelectionChrome,
} from "./ScriptClipSectionSelection";
import {
  buildScriptTimelineModel,
  calculateScriptTimelineRangeDrag,
  findScriptTimelineSectionAtTime,
  isScriptTimelineRangeDragActivated,
  normalizeScriptTimelineRange,
  type ScriptTimelineRange,
  type ScriptTimelineRangeDragMode,
  type ScriptTimelineSection,
} from "../../../librettoEditor/utils/scriptTimelineSections";
import "./ScriptClip.styles.scss";

interface ScriptClipProps {
  clipId: string;
  layerId: string;
}

type RawWaveformSource = {
  assetId: string;
  peaks: Float32Array;
  color: string;
};

type RangeDragState = {
  pointerId: number;
  mode: ScriptTimelineRangeDragMode;
  anchorTime: number;
  startClientX: number;
  initialRange: ScriptTimelineRange | null;
  activated: boolean;
  captureElement: HTMLDivElement;
};

type PendingSectionTapState = {
  pointerId: number;
  startClientX: number;
  startClientY: number;
};

const SCRIPT_TIMELINE_TAP_SLOP_PX = 6;

const releaseRangePointerCapture = (drag: RangeDragState | null) => {
  if (!drag) return;
  try {
    if (drag.captureElement.hasPointerCapture?.(drag.pointerId)) {
      drag.captureElement.releasePointerCapture(drag.pointerId);
    }
  } catch {}
};

/**
 * Remap a waveform from the clip's pre-edit time domain into the timeline domain.
 *
 * - Segments are in clip-local time (srcStartSec/srcEndSec) and map to composition time (compStartSec).
 * - Cuts collapse time by making segments pack earlier on the timeline.
 * - Gap insertions create holes by shifting downstream segments later (compStartSec jumps).
 * - Gap segments (isGap: true) are included as silence (zeros).
 */
function remapWaveformToTimeline(
  peaks: Float32Array,
  segments: ScriptLayerSegment[],
  clipDuration: number,
  timelineDuration: number,
  clipStartSec: number,
): Float32Array {
  if (!peaks.length || !segments.length || clipDuration <= 0 || timelineDuration <= 0) {
    return peaks;
  }

  const samplesPerSecond = peaks.length / clipDuration;
  const outputLength = Math.ceil(timelineDuration * samplesPerSecond);
  const output = new Float32Array(outputLength);

	for (const seg of segments) {
	  const segDuration = seg.srcEndSec - seg.srcStartSec;
	  if (segDuration <= 0) continue;

	  const compStartRel = (seg.compStartSec ?? 0) - (clipStartSec ?? 0);
	  if (!Number.isFinite(compStartRel)) continue;
	  let outStartIdx = Math.floor(compStartRel * samplesPerSecond);
	  if (!Number.isFinite(outStartIdx)) continue;
	  if (outStartIdx >= outputLength) continue;

	  let srcStartIdx = Math.max(0, Math.floor((seg.srcStartSec / clipDuration) * peaks.length));
	  const srcEndIdx = Math.min(peaks.length, Math.ceil((seg.srcEndSec / clipDuration) * peaks.length));
	  let segmentSamples = Math.max(0, srcEndIdx - srcStartIdx);

	  // Defensive: if upstream state is temporarily inconsistent (e.g. stale segments vs clipStartSec),
	  // clamp instead of crashing on negative Float32Array.set offsets.
	  if (outStartIdx < 0) {
	    const skip = Math.min(segmentSamples, -outStartIdx);
	    srcStartIdx += skip;
	    segmentSamples = Math.max(0, segmentSamples - skip);
	    outStartIdx = 0;
	  }

	  const copyLen = Math.min(segmentSamples, outputLength - outStartIdx);
	  if (copyLen <= 0) continue;

	  if (!seg.isGap) {
	    output.set(peaks.subarray(srcStartIdx, srcStartIdx + copyLen), outStartIdx);
	  }
	}

  return output;
}

// Default waveform color - dark grey
const WAVEFORM_COLOR = '#374151';
const DEFAULT_WAVEFORM_BUCKETS_PER_SECOND = 40;
const WAVEFORM_FETCH_RETRY_MS = 1500;

const normalizeState = (value: any) => String(value ?? '').toLowerCase();

const isSilentState = (value: any) => {
  const state = normalizeState(value);
  return state === 'silent' || state === 'not_needed';
};

const isTrackSilent = (track: any): boolean => {
  if (!track) return false;
  if (isSilentState(track?.transcriptionState)) return true;
  if (isSilentState(track?.enhancedTrackState)) return true;
  return false;
};

function processWaveformToClipTimeline(params: {
  fullData: Float32Array;
  sourceDurationSec: number;
  clipDurationSec: number;
  entry: any;
}): Float32Array {
  const { fullData, sourceDurationSec, clipDurationSec, entry } = params;
  if (!fullData || fullData.length === 0) return new Float32Array(0);
  if (!Number.isFinite(sourceDurationSec) || sourceDurationSec <= 0) return fullData;
  if (!Number.isFinite(clipDurationSec) || clipDurationSec <= 0) return fullData;

  const samplesPerSecond = fullData.length / sourceDurationSec;
  if (!Number.isFinite(samplesPerSecond) || samplesPerSecond <= 0) return fullData;

  const totalSamples = Math.max(1, Math.ceil(clipDurationSec * samplesPerSecond));
  const processedPeaks = new Float32Array(totalSamples);

  const entryPlaybackRate = Number(entry?.playbackRate ?? 1.0) || 1.0;

  const entrySegmentsRaw: any[] =
    Array.isArray(entry?.segments) && entry.segments.length > 0 ? entry.segments : [];

  const segments: Array<{
    offsetSec: number;
    rangeInSec: number;
    rangeOutSec: number;
    playbackRate: number;
  }> =
    entrySegmentsRaw.length > 0
      ? entrySegmentsRaw
          .map((seg) => ({
            offsetSec: Number(seg?.offsetSec ?? 0),
            rangeInSec: Number(seg?.range?.inSec ?? 0),
            rangeOutSec:
              seg?.range?.outSec == null ? Number.POSITIVE_INFINITY : Number(seg?.range?.outSec),
            playbackRate: Number(seg?.playbackRate ?? entryPlaybackRate) || 1.0,
          }))
          .filter((s) => Number.isFinite(s.offsetSec) && Number.isFinite(s.rangeInSec) && s.playbackRate > 0)
      : [
          {
            offsetSec: Number(entry?.offsetSec ?? 0),
            rangeInSec: Number(entry?.range?.inSec ?? 0),
            rangeOutSec:
              entry?.range?.outSec == null ? Number.POSITIVE_INFINITY : Number(entry?.range?.outSec),
            playbackRate: entryPlaybackRate,
          },
        ];

  segments.sort((a, b) => a.offsetSec - b.offsetSec);

  for (const seg of segments) {
    const offsetSec = seg.offsetSec;
    const playbackRate = Number(seg.playbackRate) || 1.0;
    if (!Number.isFinite(offsetSec) || !Number.isFinite(playbackRate) || playbackRate <= 0) continue;

    const rangeInSec = Math.max(0, Math.min(sourceDurationSec, Number(seg.rangeInSec) || 0));
    const rawOut = seg.rangeOutSec;
    const rangeOutSec = rawOut === Number.POSITIVE_INFINITY
      ? sourceDurationSec
      : Math.max(rangeInSec, Math.min(sourceDurationSec, Number(rawOut) || sourceDurationSec));
    const sourceRangeDuration = rangeOutSec - rangeInSec;
    if (!(sourceRangeDuration > 0)) continue;

    const startIdx = Math.floor((rangeInSec / sourceDurationSec) * fullData.length);
    const endIdx = Math.ceil((rangeOutSec / sourceDurationSec) * fullData.length);
    const slicedPeaks = fullData.subarray(Math.max(0, startIdx), Math.min(fullData.length, endIdx));
    if (slicedPeaks.length === 0) continue;

    // Timeline duration for this segment (accounts for per-segment playbackRate).
    const segmentTimelineDuration = sourceRangeDuration / playbackRate;
    if (!(segmentTimelineDuration > 0)) continue;

    const offsetSamples = Math.floor(offsetSec * samplesPerSecond);
    if (!Number.isFinite(offsetSamples)) continue;

    const segmentTimelineSamples = Math.max(1, Math.ceil(segmentTimelineDuration * samplesPerSecond));

    // Resample slicedPeaks into the timeline sample count (downsample or upsample).
    let segmentPeaks: Float32Array;
    if (playbackRate !== 1.0 && slicedPeaks.length > 0 && segmentTimelineSamples > 0) {
      segmentPeaks = new Float32Array(segmentTimelineSamples);
      const ratio = slicedPeaks.length / segmentTimelineSamples;
      for (let i = 0; i < segmentTimelineSamples; i++) {
        const srcStart = Math.floor(i * ratio);
        const srcEnd = Math.min(slicedPeaks.length, Math.floor((i + 1) * ratio));

        if (srcEnd <= srcStart) {
          const sample = slicedPeaks[Math.min(slicedPeaks.length - 1, Math.max(0, srcStart))];
          segmentPeaks[i] = sample;
          continue;
        }

        let maxVal = 0;
        for (let j = srcStart; j < srcEnd; j++) {
          if (Math.abs(slicedPeaks[j]) > Math.abs(maxVal)) {
            maxVal = slicedPeaks[j];
          }
        }
        segmentPeaks[i] = maxVal;
      }
    } else {
      segmentPeaks = slicedPeaks;
    }

    const destStart = Math.max(0, offsetSamples);
    if (destStart >= totalSamples) continue;
    const copyLength = Math.min(segmentPeaks.length, totalSamples - destStart);
    if (copyLength <= 0) continue;

    // If segments overlap, preserve the louder amplitude (max abs).
    for (let i = 0; i < copyLength; i++) {
      const prev = processedPeaks[destStart + i];
      const next = segmentPeaks[i];
      processedPeaks[destStart + i] = Math.abs(next) > Math.abs(prev) ? next : prev;
    }
  }

  return processedPeaks;
}

function inferSourceDurationSec(params: {
  entry: any;
  trackDurationSec: number;
  fullDataLength: number;
}): number {
  const trackDurationSec = Number(params.trackDurationSec ?? 0);
  if (Number.isFinite(trackDurationSec) && trackDurationSec > 0) {
    return trackDurationSec;
  }

  let maxRangeOutSec = 0;
  const segs: any[] = Array.isArray(params.entry?.segments) ? params.entry.segments : [];
  for (const seg of segs) {
    const out = Number(seg?.range?.outSec ?? Number.NaN);
    if (Number.isFinite(out) && out > maxRangeOutSec) {
      maxRangeOutSec = out;
    }
  }
  if (maxRangeOutSec <= 0) {
    const legacyOut = Number(params.entry?.range?.outSec ?? Number.NaN);
    if (Number.isFinite(legacyOut) && legacyOut > 0) {
      maxRangeOutSec = legacyOut;
    }
  }
  if (maxRangeOutSec > 0) return maxRangeOutSec;

  const fallbackFromBuckets = Number(params.fullDataLength ?? 0) / DEFAULT_WAVEFORM_BUCKETS_PER_SECOND;
  return Number.isFinite(fallbackFromBuckets) && fallbackFromBuckets > 0 ? fallbackFromBuckets : 0;
}

const ScriptClip: React.FC<ScriptClipProps> = observer(({ clipId, layerId }) => {
  const [filename, setFilename] = useState<string | undefined>(CompositionStateStore.getClipTitle(clipId));
  const [peaks, setPeaks] = useState<Float32Array | null>(null);
  const [rawMultiSourceWaveforms, setRawMultiSourceWaveforms] = useState<RawWaveformSource[]>([]);
  const { GetWaveformData } = useAssetStore();
  const compositionUpdateTimestamp = CompositionStateStore.updateTimestamp;

  // Scene hover state
  const [isHovered, setIsHovered] = useState(false);
  const [hoveredSceneIndex, setHoveredSceneIndex] = useState<number | null>(null);
  const clipContainerRef = useRef<HTMLDivElement>(null);
  const rangeDragRef = useRef<RangeDragState | null>(null);
	const pendingSectionTapRef = useRef<PendingSectionTapState | null>(null);
  const [rangePreview, setRangePreview] = useState<ScriptTimelineRange | null>(null);

  // Studio Sound state for waveform selection (original vs denoised)
  const [studioSoundEnabled, setStudioSoundEnabled] = useState(() => {
    const clip: any = CompositionService.getClipById(clipId);
    return Boolean(clip?.studioSoundEnabled);
  });

  // Track selection state from ApplicationStore for scene-level focus
  const isFocused = ApplicationStore.selectedClipId === clipId;
  const selectedSceneIndex = ApplicationStore.selectedSceneIndex;
  const selectedScriptSection = ApplicationStore.selectedScriptSection;
  const selectedTranscriptRange = ApplicationStore.selectedTranscriptTimelineRange;
  const selectedToolRange = ApplicationStore.selectedScriptLayerRange;
  const rangeToolEnabled = ApplicationStore.rangeToolEnabled;

  // Watch for filename changes
  useEffect(() => {
    const disposer = reaction(
      () => ({ filename: CompositionStateStore.getClipTitle(clipId) }),
      ({ filename }) => setFilename(filename),
      { fireImmediately: true }
    );
    return () => disposer();
  }, [clipId]);

  // Listen for studioSoundChanged events from the engine clip
  useEffect(() => {
    const clip = CompositionService.getClipById(clipId) as any;
    if (!clip) return;

    const handleStudioSoundChanged = (event: any) => {
      const data = event?.detail ?? event;
      setStudioSoundEnabled(Boolean(data?.enabled));
      // Clear fetched waveforms so they get re-fetched with new denoised flag
      fetchedRef.current.waveformFetchKeys.clear();
      setPeaks(null);
      setRawMultiSourceWaveforms([]);
    };

    const listenerId = clip.on?.('studioSoundChanged', handleStudioSoundChanged);

    // Initialize from current state
    if (clip.studioSoundEnabled !== undefined) {
      setStudioSoundEnabled(Boolean(clip.studioSoundEnabled));
      // Clear any cached waveforms so we fetch the correct variant if the clip was restored.
      fetchedRef.current.waveformFetchKeys.clear();
    }

    return () => {
      if (listenerId) clip.off?.(listenerId);
    };
  }, [clipId]);

  // Track what we've already fetched to prevent infinite loops.
  // Keyed by "<assetId>:<variant>" where variant is "original" | "enhanced".
  const fetchedRef = useRef<{
    waveformFetchKeys: Set<string>;
  }>({ waveformFetchKeys: new Set() });
  const waveformFetchSeq = useRef(0);
  const waveformRetryTimerRef = useRef<Map<string, number>>(new Map());
  const waveformRetryAttemptsRef = useRef<Map<string, number>>(new Map());
  const [waveformRetryTick, setWaveformRetryTick] = useState(0);
  const scriptSourceSignature = (() => {
    const clip: any = CompositionService.getClipById(clipId);
    if (!clip || String(clip.type) !== "script") return "";
    return getScriptClipAudioEntriesSignature(clip);
  })();

  // Watch for waveform availability for multi-source clips
	  useEffect(() => {
	    const seq = ++waveformFetchSeq.current;
    const clearRetryTimers = () => {
      for (const timer of waveformRetryTimerRef.current.values()) {
        window.clearTimeout(timer);
      }
      waveformRetryTimerRef.current.clear();
    };
    clearRetryTimers();

    const scheduleWaveformRetry = (key: string) => {
      if (!key) return;
      if (waveformFetchSeq.current !== seq) return;
      if (waveformRetryTimerRef.current.has(key)) return;

      const attempts = Number(waveformRetryAttemptsRef.current.get(key) ?? 0);
      waveformRetryAttemptsRef.current.set(key, attempts + 1);
      const retryDelayMs = Math.min(WAVEFORM_FETCH_RETRY_MS + (attempts * 500), 6000);

      const timer = window.setTimeout(() => {
        waveformRetryTimerRef.current.delete(key);
        if (waveformFetchSeq.current !== seq) return;
        setWaveformRetryTick((v) => v + 1);
      }, retryDelayMs);
      waveformRetryTimerRef.current.set(key, timer);
    };

    const markWaveformFetchSuccess = (key: string) => {
      if (!key) return;
      const timer = waveformRetryTimerRef.current.get(key);
      if (timer != null) {
        window.clearTimeout(timer);
        waveformRetryTimerRef.current.delete(key);
      }
      waveformRetryAttemptsRef.current.delete(key);
    };

    const clip: any = CompositionService.getClipById(clipId);
    if (!clip) return () => { clearRetryTimers(); };

	    if (clip.type === 'script' && typeof clip.getAudioEntries === 'function') {
	      const audioEntries = clip.getAudioEntries();
	      if (audioEntries && audioEntries.length > 0) {
	        const disposers: Array<() => void> = [];

	        audioEntries.forEach((entry: any, index: number) => {
	          const assetId = entry?.assetId;
	          if (!assetId) return;

	          const disposer = reaction(
	            () => {
	              const t = ProjectAssetsStore.projectTracks.find((x: any) => x.id === assetId || x.trackId === assetId);
	              const durationFromStore = CompositionStateStore.clips?.[clipId]?.duration ?? 0;
	              const clipDuration = Number.isFinite(durationFromStore)
	                ? Number(durationFromStore)
	                : (clip.duration?.seconds ?? clip.timelineDuration?.seconds ?? 0);
	              // Check appropriate waveform availability based on Studio Sound state
	              const useDenoised = studioSoundEnabled && !isTrackSilent(t);
	              const hasWaveform = useDenoised
	                ? t?.enhancedTrackState === 'ready'
	                : t?.hasWaveform === true;
	              return { hasWaveform, clipDuration, useDenoised };
	            },
	            async ({ hasWaveform, clipDuration, useDenoised }) => {
                  const waveformFetchKey = `${assetId}:${useDenoised ? 'enhanced' : 'original'}`;
	              if (hasWaveform && !fetchedRef.current.waveformFetchKeys.has(waveformFetchKey) && clipDuration > 0) {
	                fetchedRef.current.waveformFetchKeys.add(waveformFetchKey);
	                try {
	                  const fullData = await GetWaveformData({ trackId: assetId, denoised: useDenoised });
	                  if (waveformFetchSeq.current !== seq) {
                        fetchedRef.current.waveformFetchKeys.delete(waveformFetchKey);
                        return;
                      }
	                  if (fullData && fullData.length) {
                    markWaveformFetchSuccess(waveformFetchKey);
                    setRawMultiSourceWaveforms(prev => {
                      if (waveformFetchSeq.current !== seq) return prev;
                      const updated = [...prev];
                      while (updated.length <= index) {
                        updated.push({ assetId: "", peaks: new Float32Array(0), color: WAVEFORM_COLOR });
                      }
	                      updated[index] = { assetId, peaks: fullData, color: WAVEFORM_COLOR };
	                      return updated;
	                    });
	                  } else {
	                    fetchedRef.current.waveformFetchKeys.delete(waveformFetchKey);
                    scheduleWaveformRetry(waveformFetchKey);
	                  }
	                } catch (e) {
	                  console.warn('[ScriptClip] Failed to fetch waveform for source', assetId, e);
	                  fetchedRef.current.waveformFetchKeys.delete(waveformFetchKey);
                  scheduleWaveformRetry(waveformFetchKey);
	                }
	              }
	            },
            { fireImmediately: true }
          );
          disposers.push(disposer);
        });

        return () => {
          disposers.forEach(d => d());
          clearRetryTimers();
        };
      }
    }

    // Single-source clip reaction
    const trackId = CompositionStateStore.clipIdToAssetId?.[clipId];
    if (!trackId) return () => { clearRetryTimers(); };

    const disposer = reaction(
      () => {
        const t = ProjectAssetsStore.projectTracks.find((x: any) => x.id === trackId || x.trackId === trackId);
        const useDenoised = studioSoundEnabled && !isTrackSilent(t);
        return {
          hasWaveform: useDenoised
            ? t?.enhancedTrackState === 'ready'
            : t?.hasWaveform === true,
          useDenoised,
        };
      },
	      async ({ hasWaveform, useDenoised }) => {
            const waveformFetchKey = `${trackId}:${useDenoised ? 'enhanced' : 'original'}`;
	        if (hasWaveform && !fetchedRef.current.waveformFetchKeys.has(waveformFetchKey)) {
	          fetchedRef.current.waveformFetchKeys.add(waveformFetchKey);
	          try {
	            const data = await GetWaveformData({ trackId, denoised: useDenoised });
	            if (waveformFetchSeq.current !== seq) {
                  fetchedRef.current.waveformFetchKeys.delete(waveformFetchKey);
                  return;
                }
	            if (data && data.length) {
                markWaveformFetchSuccess(waveformFetchKey);
	              setPeaks(data);
	            } else {
	              fetchedRef.current.waveformFetchKeys.delete(waveformFetchKey);
                scheduleWaveformRetry(waveformFetchKey);
	            }
	          } catch {
	            fetchedRef.current.waveformFetchKeys.delete(waveformFetchKey);
              scheduleWaveformRetry(waveformFetchKey);
	          }
	        }
	      },
	      { fireImmediately: true }
	    );
	    return () => {
        disposer();
        clearRetryTimers();
      };
	  }, [clipId, studioSoundEnabled, waveformRetryTick, compositionUpdateTimestamp, scriptSourceSignature]);

  // Single-source waveform peaks (pre-edit domain)
  const primaryTrackId = CompositionStateStore.clipIdToAssetId?.[clipId];
  const primaryWaveformProgress = WaveformProgressStore.get(primaryTrackId);
  const displayPeaksVersion = primaryWaveformProgress?.version ?? 0;
  const displayPeaks =
    peaks && peaks.length
      ? peaks
      : primaryWaveformProgress?.peaks && primaryWaveformProgress.peaks.length
        ? primaryWaveformProgress.peaks
        : null;

  const duration = CompositionStateStore.clips?.[clipId]?.trimmedDuration ?? 0;

  // Effective duration for waveform display (timeline duration)
  // Note: CompositionStateStore.trimmedDuration is already timeline duration
  // (set from clip.timelineDuration which accounts for playbackRate)
  const effectiveDuration = duration;

  // Clip duration in the pre-edit "script spine" domain (before cuts collapse time).
  const clipDurationForWaveform =
    CompositionStateStore.clips?.[clipId]?.duration ??
    (CompositionService.getClipById(clipId) as any)?.duration?.seconds ??
    0;

  // Remap raw per-source waveforms (asset domain) into the script-clip timeline domain.
  // This respects per-source multi-segment placement (empty scenes / sequential sources).
  const processedMultiSourceWaveforms = useMemo(() => {
    const engineClip: any = CompositionService.getClipById(clipId);
    if (!engineClip || String(engineClip.type) !== 'script' || typeof engineClip.getAudioEntries !== 'function') {
      return [];
    }
    const audioEntries: any[] = engineClip.getAudioEntries() ?? [];
    if (!audioEntries.length) return [];

    const clipDuration = Number(clipDurationForWaveform ?? 0);
    if (!Number.isFinite(clipDuration) || clipDuration <= 0) return [];

    const byAssetId = new Map<string, Float32Array>();
    for (const w of rawMultiSourceWaveforms) {
      if (w?.assetId && w.peaks && w.peaks.length) {
        byAssetId.set(w.assetId, w.peaks);
      }
    }
    // While local waveform/LUFS compute is still running, we can show partial peaks
    // from the in-memory progress store (fills in progressively over time).
    for (const entry of audioEntries) {
      const assetId = entry?.assetId;
      if (!assetId) continue;
      if (byAssetId.has(assetId)) continue;
      const progress = WaveformProgressStore.get(assetId);
      if (progress?.peaks && progress.peaks.length) {
        byAssetId.set(assetId, progress.peaks);
      }
    }

    const out: Array<{ peaks: Float32Array; color: string }> = [];
    for (let sourceIndex = 0; sourceIndex < audioEntries.length; sourceIndex++) {
      const entry = audioEntries[sourceIndex];
      const assetId = entry?.assetId;
      const fullData = assetId ? byAssetId.get(assetId) : undefined;
      if (!assetId || !fullData || fullData.length === 0) {
        out[sourceIndex] = { peaks: new Float32Array(0), color: WAVEFORM_COLOR };
        continue;
      }

      const trackRecord = ProjectAssetsStore.projectTracks.find(
        (t: any) => t.id === assetId || t.trackId === assetId
      );
      const sourceDurationSec = inferSourceDurationSec({
        entry,
        trackDurationSec: Number(trackRecord?.duration ?? 0),
        fullDataLength: fullData.length,
      });

      out[sourceIndex] = {
        peaks: processWaveformToClipTimeline({
          fullData,
          sourceDurationSec,
          clipDurationSec: clipDuration,
          entry,
        }),
        color: WAVEFORM_COLOR,
      };
    }
    return out;
  }, [
    clipId,
    rawMultiSourceWaveforms,
    clipDurationForWaveform,
    scriptSourceSignature,
    ScriptLayerStore.cutsVersion,
    ScriptLayerStore.scenesVersion,
    ProjectAssetsStore.projectTracks,
    WaveformProgressStore.version,
  ]);

  // Engine scenes (split-derived) for per-scene gain visualization in the waveform.
  const scenes = useMemo((): ScriptLayerScene[] => {
    const clip: any = CompositionService.getClipById(clipId);
    if (!clip || typeof clip.getScenes !== 'function') return [];
    return clip.getScenes();
  }, [clipId, ScriptLayerStore.cutsVersion, ScriptLayerStore.scenesVersion, effectiveDuration]);

  const timelineModel = useMemo(() => {
    const clip: any = CompositionService.getClipById(clipId);
    return buildScriptTimelineModel({
      clip,
      duration: effectiveDuration,
      mapSourceBoundaryToTimeline: (sourceTime) =>
        ScriptLayerStore.mapSourceTimeToCompositionTimeForBoundary(sourceTime),
    });
  }, [
    clipId,
    effectiveDuration,
    ScriptLayerStore.cutsVersion,
    ScriptLayerStore.scenesVersion,
    ScriptLayerStore.playbackRate,
  ]);

  const hoverSections = timelineModel.sections;
  const gapInsertionSections = timelineModel.gaps;

  const resolvedSelectedScriptSection = useMemo((): ScriptTimelineSection | null => {
    if (!selectedScriptSection) return null;

    if (selectedScriptSection.kind === 'scene') {
      if (selectedScriptSection.sceneIdentity) {
        const sameSceneSections = hoverSections.filter(
          (section) =>
            section.kind === 'scene' &&
            section.sceneIdentity === selectedScriptSection.sceneIdentity,
        );
        return sameSceneSections.find(
          (section) => section.id === selectedScriptSection.id,
        ) ?? sameSceneSections[0] ?? null;
      }

      const legacySectionWithSameId = hoverSections.find(
        (section) => section.kind === 'scene' && section.id === selectedScriptSection.id,
      );
      if (legacySectionWithSameId) return legacySectionWithSameId;

      if (typeof selectedScriptSection.sceneIndex === 'number') {
        return hoverSections.find(
          (section) =>
            section.kind === 'scene' &&
            section.sceneIndex === selectedScriptSection.sceneIndex,
        ) ?? null;
      }

      return null;
    }

    if (selectedScriptSection.gapInsertionId) {
      return hoverSections.find(
        (section) =>
          section.kind === 'gap' &&
          section.gapInsertionId === selectedScriptSection.gapInsertionId,
      ) ?? null;
    }

    return hoverSections.find(
      (section) => section.kind === 'gap' && section.id === selectedScriptSection.id,
    ) ?? null;
  }, [hoverSections, selectedScriptSection]);

  useEffect(() => {
    if (!isFocused || !selectedScriptSection || effectiveDuration <= 0) return;

    if (!resolvedSelectedScriptSection) {
      ApplicationStore.clearSelectedScriptTimelineSection();
      return;
    }

    const selectionIsCurrent =
      selectedScriptSection.id === resolvedSelectedScriptSection.id &&
      selectedScriptSection.kind === resolvedSelectedScriptSection.kind &&
      selectedScriptSection.start === resolvedSelectedScriptSection.start &&
      selectedScriptSection.end === resolvedSelectedScriptSection.end &&
      selectedScriptSection.sceneIndex === resolvedSelectedScriptSection.sceneIndex &&
      selectedScriptSection.sceneIdentity === resolvedSelectedScriptSection.sceneIdentity &&
      selectedScriptSection.label === resolvedSelectedScriptSection.label &&
      selectedScriptSection.gapInsertionId === resolvedSelectedScriptSection.gapInsertionId;

    if (!selectionIsCurrent) {
      ApplicationStore.selectScriptTimelineSection(
        resolvedSelectedScriptSection,
        hoverSections,
      );
    }
  }, [
    effectiveDuration,
    hoverSections,
    isFocused,
    resolvedSelectedScriptSection,
    selectedScriptSection,
  ]);

  const getTimeAtClientX = useCallback((clientX: number): number => {
    if (!clipContainerRef.current || effectiveDuration <= 0) return 0;
    const rect = clipContainerRef.current.getBoundingClientRect();
    if (rect.width <= 0) return 0;
    const percentage = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return percentage * effectiveDuration;
  }, [effectiveDuration]);

	const selectSectionAndSeekAtClientX = useCallback((clientX: number) => {
		const pointerTime = getTimeAtClientX(clientX);
		if (ApplicationStore.selectedClipId !== clipId) {
			ApplicationStore.setSelectedClipId(clipId);
		}
		const section = findScriptTimelineSectionAtTime(hoverSections, pointerTime);
		if (section) ApplicationStore.selectScriptTimelineSection(section, hoverSections);

		const clipStartSec = Number(
			CompositionStateStore.clips?.[clipId]?.startTime ??
			CompositionService.getClipById(clipId)?.start?.seconds ??
			0,
		);
		CompositionService.seek(clipStartSec + pointerTime);
	}, [clipId, getTimeAtClientX, hoverSections]);

  const updateHoveredSectionAtClientX = useCallback((clientX: number) => {
    if (!clipContainerRef.current || !effectiveDuration || hoverSections.length <= 1) {
      setHoveredSceneIndex(null);
      return;
    }

    const section = findScriptTimelineSectionAtTime(hoverSections, getTimeAtClientX(clientX));
    setHoveredSceneIndex(section ? hoverSections.findIndex((candidate) => candidate.id === section.id) : null);
  }, [effectiveDuration, getTimeAtClientX, hoverSections]);

  const handleMouseEnter = (event: React.MouseEvent<HTMLDivElement>) => {
    setIsHovered(true);
    if (!rangeToolEnabled) updateHoveredSectionAtClientX(event.clientX);
  };
  const handleMouseLeave = () => {
    setIsHovered(false);
    setHoveredSceneIndex(null);
  };

  // Each scene boundary has separate outgoing and incoming transition controls.
  const sceneBoundaryTransitions = useMemo(() => {
    const engineClip: any = CompositionService.getClipById(clipId);
    if (!engineClip || effectiveDuration <= 0) return [];
    return timelineModel.sceneBoundaries.map((boundary) => ({
      boundarySceneIndex: boundary.sceneIndex,
      prevSceneIndex: boundary.previousSceneIndex,
      position: boundary.time / effectiveDuration,
      outTransition:
        engineClip.getSceneOutTransition?.(boundary.previousSceneIndex) ?? DEFAULT_SCENE_TRANSITION,
      inTransition:
        engineClip.getSceneInTransition?.(boundary.sceneIndex) ?? DEFAULT_SCENE_TRANSITION,
    }));
  }, [clipId, effectiveDuration, timelineModel.sceneBoundaries]);

  const nonSceneSplitBoundaries = timelineModel.editBoundaries.map((boundary) => boundary.time);

  const hoveredContentSceneIndex = useMemo(() => {
    if (hoveredSceneIndex == null || hoveredSceneIndex < 0) return null;
    const section = hoverSections[hoveredSceneIndex];
    if (!section) return null;
    return section.kind === 'scene' ? section.sceneIndex : null;
  }, [hoveredSceneIndex, hoverSections]);

  // Handle out-transition config change (how a scene ends)
  const handleOutTransitionChange = useCallback((
    sceneIndex: number,
    audioConfig?: UITransitionConfig,
    visualConfig?: VisualTransitionConfig
  ) => {
    const engineClip: any = CompositionService.getClipById(clipId);
    if (!engineClip || !engineClip.setSceneOutTransition) return;

    const currentTransition = engineClip.getSceneOutTransition?.(sceneIndex) ?? DEFAULT_SCENE_TRANSITION;

    const newTransition: TransitionConfig = {
      audio: audioConfig ? {
        type: audioConfig.type as any,
        durationMs: audioConfig.durationSec * 1000,
      } : currentTransition.audio,
      video: visualConfig ? {
        // UI uses 'crossfade' where engine uses 'dissolve'
        type: (
          visualConfig.type === 'crossfade'
            ? 'dissolve'
            : visualConfig.type === 'none'
              ? 'cut'
              : visualConfig.type === 'blur'
                ? 'dissolve'
                : visualConfig.type
        ) as any,
        durationMs: (visualConfig.type === 'none' ? 0 : visualConfig.durationSec * 1000),
      } : currentTransition.video,
    };

    engineClip.setSceneOutTransition(sceneIndex, newTransition);

    // For crossfade, also set the in-transition of the next scene
    if (audioConfig?.type === 'crossfade') {
      engineClip.setSceneInTransition?.(sceneIndex + 1, newTransition);
    }

    // Persist sceneProperties changes (transitions) and refresh schedule.
    try { engineClip.track?.composition?.onClipTimingChanged?.(); } catch {}
    try { engineClip.track?.composition?.trigger?.(CompositionEventsEnum.CLIP_UPDATED, { clip: engineClip }); } catch {}

    ScriptLayerStore.bumpCutsVersion();
  }, [clipId]);

  // Handle in-transition config change (how a scene begins)
  const handleInTransitionChange = useCallback((
    sceneIndex: number,
    audioConfig?: UITransitionConfig,
    visualConfig?: VisualTransitionConfig
  ) => {
    const engineClip: any = CompositionService.getClipById(clipId);
    if (!engineClip || !engineClip.setSceneInTransition) return;

    const currentTransition = engineClip.getSceneInTransition?.(sceneIndex) ?? DEFAULT_SCENE_TRANSITION;

    const newTransition: TransitionConfig = {
      audio: audioConfig ? {
        type: audioConfig.type as any,
        durationMs: audioConfig.durationSec * 1000,
      } : currentTransition.audio,
      video: visualConfig ? {
        // UI uses 'crossfade' where engine uses 'dissolve'
        type: (
          visualConfig.type === 'crossfade'
            ? 'dissolve'
            : visualConfig.type === 'none'
              ? 'cut'
              : visualConfig.type === 'blur'
                ? 'dissolve'
                : visualConfig.type
        ) as any,
        durationMs: (visualConfig.type === 'none' ? 0 : visualConfig.durationSec * 1000),
      } : currentTransition.video,
    };

    engineClip.setSceneInTransition(sceneIndex, newTransition);

    // For crossfade, also set the out-transition of the previous scene
    if (audioConfig?.type === 'crossfade' && sceneIndex > 0) {
      engineClip.setSceneOutTransition?.(sceneIndex - 1, newTransition);
    }

    // Persist sceneProperties changes (transitions) and refresh schedule.
    try { engineClip.track?.composition?.onClipTimingChanged?.(); } catch {}
    try { engineClip.track?.composition?.trigger?.(CompositionEventsEnum.CLIP_UPDATED, { clip: engineClip }); } catch {}

    ScriptLayerStore.bumpCutsVersion();
  }, [clipId]);

  // Convert engine TransitionConfig to UI TransitionConfig
  const toUIAudioConfig = (transition?: TransitionConfig): UITransitionConfig => ({
    type: (transition?.audio?.type === 'crossfade' ? 'crossfade' : 'fade') as 'fade' | 'crossfade',
    durationSec: (transition?.audio?.durationMs ?? 0) / 1000,
  });

  const toUIVisualConfig = (transition?: TransitionConfig): VisualTransitionConfig => {
    const engineType = transition?.video?.type;
    const type: VisualTransitionConfig['type'] =
      engineType === 'dissolve'
        ? 'crossfade'
        : engineType === 'fade'
          ? 'fade'
          : engineType === 'wipe'
            ? 'wipe'
            : 'none';
    return {
      type,
      durationSec: (transition?.video?.durationMs ?? 0) / 1000,
    };
  };

  // Read the engine clip for waveform remapping metadata.
  const clip: any = CompositionService.getClipById(clipId);

  // Get clip gain for visual scaling from observable store (default to 1.0)
  // This is observable via MobX, so changes trigger re-render
  const clipGain = ScriptLayerStore.getClipGain(clipId);

  // Observe cuts and segments from ScriptLayerStore
  const segments = ScriptLayerStore.segments;
  const _cutsVersion = ScriptLayerStore.cutsVersion;

  // Get the original clip duration (before cuts) for waveform collapsing
  const originalDuration = clip?.duration?.seconds ?? 0;
  const clipStartSec = clip?.start?.seconds ?? 0;

  // Collapse waveforms to only show playable segments
  const collapsedMultiSourceWaveforms = useMemo(() => {
    if (!segments.length || !processedMultiSourceWaveforms.length || originalDuration <= 0 || effectiveDuration <= 0) {
      return processedMultiSourceWaveforms;
    }
    return processedMultiSourceWaveforms.map(source => ({
      ...source,
      peaks: remapWaveformToTimeline(source.peaks, segments, originalDuration, effectiveDuration, clipStartSec),
    }));
  }, [processedMultiSourceWaveforms, segments, originalDuration, effectiveDuration, clipStartSec, _cutsVersion]);

  // Collapse single-source waveform
  const collapsedDisplayPeaks = useMemo(() => {
    if (!segments.length || !displayPeaks || originalDuration <= 0 || effectiveDuration <= 0) {
      return displayPeaks;
    }
    return remapWaveformToTimeline(displayPeaks, segments, originalDuration, effectiveDuration, clipStartSec);
  }, [displayPeaks, displayPeaksVersion, segments, originalDuration, effectiveDuration, clipStartSec, _cutsVersion]);

  // Merge multi-source waveforms into a single waveform for display
  const mergedPeaks = useMemo(() => {
    if (collapsedMultiSourceWaveforms.length === 0) {
      return collapsedDisplayPeaks;
    }

    const maxLength = Math.max(...collapsedMultiSourceWaveforms.map(s => s.peaks?.length ?? 0));
    if (maxLength === 0) return null;

    const merged = new Float32Array(maxLength);
    for (let i = 0; i < maxLength; i++) {
      let maxVal = 0;
      for (const source of collapsedMultiSourceWaveforms) {
        if (source.peaks && i < source.peaks.length) {
          if (Math.abs(source.peaks[i]) > Math.abs(maxVal)) {
            maxVal = source.peaks[i];
          }
        }
      }
      merged[i] = maxVal;
    }

    return merged;
  }, [collapsedMultiSourceWaveforms, collapsedDisplayPeaks]);

  // Some video files legitimately have no audio stream, so waveform data will never arrive.
  // In that case, don't show a stuck "Processing..." state forever.
  const shouldExpectWaveform = useMemo(() => {
    const clip: any = CompositionService.getClipById(clipId);
    if (!clip) return true;

    // The engine can often tell us immediately if there's any audio across sources.
    // If it says there's no audio, don't wait on local/remote waveform state.
    if (clip.hasAudioStream === false) return false;
    if (clip.hasAudioStream === true) return true;

    const assetIds = new Set<string>();

    // Prefer explicit audio entries (multi-source ScriptClip).
    if (clip.type === 'script' && typeof clip.getAudioEntries === 'function') {
      for (const entry of clip.getAudioEntries() ?? []) {
        const assetId = entry?.assetId;
        if (assetId) assetIds.add(assetId);
      }
    }

    // Fallback: single-source mapping.
    const mappedTrackId = CompositionStateStore.clipIdToAssetId?.[clipId];
    if (mappedTrackId) assetIds.add(mappedTrackId);

    // Final fallback: video sources (covers video-without-audio where getAudioEntries() can be empty).
    if (assetIds.size === 0 && clip.type === 'script' && typeof clip.getVideoSources === 'function') {
      for (const videoSource of clip.getVideoSources() ?? []) {
        const source = videoSource?.source;
        const id = source && 'id' in source ? (source as any).id : undefined;
        if (id) assetIds.add(id);
      }
    }

    // If we can't resolve the underlying assets yet, be conservative and assume a waveform is expected.
    if (assetIds.size === 0) return true;

    const trackRecords = Array.from(assetIds)
      .map((id) => ProjectAssetsStore.projectTracks.find((t: any) => t.id === id || t.trackId === id))
      .filter(Boolean);

    // Tracks snapshot may not have arrived yet; assume expected until we can confirm otherwise.
    if (trackRecords.length === 0) return true;

    const trackExpectsWaveform = (t: any): boolean => {
      const hasAudioStream = t?.hasAudioStream;
      if (hasAudioStream === false) return false;
      if (hasAudioStream === true) return true;

      const stateLower = String(t?.transcriptionState || '').toLowerCase();
      if (stateLower === 'not_needed' || stateLower === 'silent') return false;
      return true;
    };

    return trackRecords.some(trackExpectsWaveform);
  }, [clipId, ProjectAssetsStore.projectTracks]);

  // For clips with no audio stream, show a flat (all-zero) waveform line instead of "Processing...".
  const silentPeaks = useMemo(() => {
    if (shouldExpectWaveform) return null;
    if (effectiveDuration <= 0) return null;
    const peaksPerSecond = 40; // matches legacy TimelineWaveform behavior
    const length = Math.max(1, Math.ceil(effectiveDuration * peaksPerSecond));
    return new Float32Array(length);
  }, [shouldExpectWaveform, effectiveDuration]);

  // Use merged peaks if available, otherwise single-source peaks, otherwise silent fallback.
  const finalPeaks = mergedPeaks || collapsedDisplayPeaks || silentPeaks;

  // Check if waveform is processing (no peaks yet, but we expect it to arrive).
  const isProcessing = shouldExpectWaveform && !finalPeaks && duration > 0;

  const transcriptHighlightRange = useMemo(
    () =>
      isFocused && !rangeToolEnabled
        ? normalizeScriptTimelineRange(selectedTranscriptRange, effectiveDuration)
        : null,
    [effectiveDuration, isFocused, rangeToolEnabled, selectedTranscriptRange],
  );

  const activeSelectionChrome = resolveScriptClipSelectionChrome({
    rangeToolEnabled,
    hasTranscriptSelection: transcriptHighlightRange !== null,
  });

  const selectedSceneSections = useMemo((): ScriptTimelineSection[] => {
    if (
      !isFocused ||
      effectiveDuration <= 0 ||
      activeSelectionChrome !== 'scene'
    ) return [];

    if (resolvedSelectedScriptSection?.kind === 'gap') {
      return [resolvedSelectedScriptSection];
    }

    if (
      resolvedSelectedScriptSection?.kind === 'scene' &&
      resolvedSelectedScriptSection.sceneIdentity
    ) {
      return hoverSections.filter(
        (section) =>
          section.kind === 'scene' &&
          section.sceneIdentity === resolvedSelectedScriptSection.sceneIdentity,
      );
    }

    if (
      resolvedSelectedScriptSection?.kind === 'scene' &&
      typeof resolvedSelectedScriptSection.sceneIndex === 'number'
    ) {
      return hoverSections.filter(
        (section) =>
          section.kind === 'scene' &&
          section.sceneIndex === resolvedSelectedScriptSection.sceneIndex,
      );
    }

	// Numeric scene selection is a legacy/external entry path only. If a stable
	// section failed reconciliation, never let the old index briefly paint a
	// different scene before the cleanup effect runs.
	if (
		!selectedScriptSection &&
		selectedSceneIndex !== null &&
		Number.isFinite(selectedSceneIndex)
	) {
      return hoverSections.filter(
        (section) => section.kind === 'scene' && section.sceneIndex === selectedSceneIndex,
      );
    }

    return [];
  }, [
    effectiveDuration,
    hoverSections,
    isFocused,
    activeSelectionChrome,
    resolvedSelectedScriptSection,
    selectedSceneIndex,
	selectedScriptSection,
  ]);

  const toolHighlightRange = useMemo(
    () =>
      rangeToolEnabled
        ? normalizeScriptTimelineRange(rangePreview ?? selectedToolRange, effectiveDuration)
        : null,
    [effectiveDuration, rangePreview, rangeToolEnabled, selectedToolRange],
  );

  const updateRangePreview = useCallback((drag: RangeDragState, clientX: number) => {
    if (!drag.activated) {
      drag.activated = isScriptTimelineRangeDragActivated(
        drag.mode,
        drag.startClientX,
        clientX,
      );
      if (!drag.activated) return;
    }
    const pointerTime = getTimeAtClientX(clientX);
    setRangePreview(calculateScriptTimelineRangeDrag({
      mode: drag.mode,
      anchorTime: drag.anchorTime,
      pointerTime,
      duration: effectiveDuration,
      initialRange: drag.initialRange,
      activated: drag.activated,
    }));
  }, [effectiveDuration, getTimeAtClientX]);

  const handlePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || event.isPrimary === false || rangeDragRef.current) return;
    if ((event.target as HTMLElement).closest('[data-script-clip-control="true"]')) return;
	const isMousePointer = !event.pointerType || event.pointerType === 'mouse';

	// Let touch/pen gestures retain native timeline panning. Selection is
	// committed on pointer-up only if the gesture stayed within tap slop.
	if (!rangeToolEnabled && !isMousePointer) {
		event.stopPropagation();
		pendingSectionTapRef.current = {
			pointerId: event.pointerId,
			startClientX: event.clientX,
			startClientY: event.clientY,
		};
		return;
	}

    event.preventDefault();
    event.stopPropagation();

    const pointerTime = getTimeAtClientX(event.clientX);
    if (!rangeToolEnabled) {
		selectSectionAndSeekAtClientX(event.clientX);
      return;
    }

    if (ApplicationStore.selectedClipId !== clipId) {
      ApplicationStore.setSelectedClipId(clipId);
    }

    const actionElement = (event.target as HTMLElement).closest<HTMLElement>('[data-range-action]');
    const action = actionElement?.dataset.rangeAction;
    const initialRange = normalizeScriptTimelineRange(
      ApplicationStore.selectedScriptLayerRange,
      effectiveDuration,
    );
    const mode: RangeDragState['mode'] =
      action === 'resize-start' || action === 'resize-end' || action === 'move'
        ? action
        : 'create';

    const drag: RangeDragState = {
      pointerId: event.pointerId,
      mode,
      anchorTime: pointerTime,
      startClientX: event.clientX,
      initialRange,
      activated: mode !== 'create',
      captureElement: event.currentTarget,
    };
    rangeDragRef.current = drag;
    // Preserve the prior selection until a create drag passes the threshold.
    setRangePreview(initialRange);
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
	} catch {
		// Without capture, leaving the element can strand this gesture with no
		// pointer-up/cancel. Abort cleanly so the next drag is never blocked.
		rangeDragRef.current = null;
		setRangePreview(null);
	}
	}, [clipId, effectiveDuration, getTimeAtClientX, rangeToolEnabled, selectSectionAndSeekAtClientX]);

  const handlePointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
	const pendingTap = pendingSectionTapRef.current;
	if (pendingTap?.pointerId === event.pointerId) {
		const distance = Math.hypot(
			event.clientX - pendingTap.startClientX,
			event.clientY - pendingTap.startClientY,
		);
		if (distance > SCRIPT_TIMELINE_TAP_SLOP_PX) {
			pendingSectionTapRef.current = null;
		}
		return;
	}
    const drag = rangeDragRef.current;
    if (drag && drag.pointerId === event.pointerId) {
      event.preventDefault();
      event.stopPropagation();
      updateRangePreview(drag, event.clientX);
      return;
    }
    if (!rangeToolEnabled) updateHoveredSectionAtClientX(event.clientX);
  }, [rangeToolEnabled, updateHoveredSectionAtClientX, updateRangePreview]);

  const finishRangeDrag = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const drag = rangeDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    event.preventDefault();
    event.stopPropagation();
    drag.activated =
      drag.activated ||
      isScriptTimelineRangeDragActivated(drag.mode, drag.startClientX, event.clientX);
    const nextRange = calculateScriptTimelineRangeDrag({
      mode: drag.mode,
      anchorTime: drag.anchorTime,
      pointerTime: getTimeAtClientX(event.clientX),
      duration: effectiveDuration,
      initialRange: drag.initialRange,
      activated: drag.activated,
    });

    ApplicationStore.setSelectedScriptLayerRange(nextRange);
    rangeDragRef.current = null;
    setRangePreview(null);
    releaseRangePointerCapture(drag);
  }, [effectiveDuration, getTimeAtClientX]);

	const handlePointerUp = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
		const pendingTap = pendingSectionTapRef.current;
		if (pendingTap?.pointerId === event.pointerId) {
			pendingSectionTapRef.current = null;
			const distance = Math.hypot(
				event.clientX - pendingTap.startClientX,
				event.clientY - pendingTap.startClientY,
			);
			if (distance <= SCRIPT_TIMELINE_TAP_SLOP_PX) {
				event.preventDefault();
				event.stopPropagation();
				selectSectionAndSeekAtClientX(event.clientX);
			}
			return;
		}
		finishRangeDrag(event);
	}, [finishRangeDrag, selectSectionAndSeekAtClientX]);

  const cancelRangeDrag = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
	if (pendingSectionTapRef.current?.pointerId === event.pointerId) {
		pendingSectionTapRef.current = null;
	}
    const drag = rangeDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    rangeDragRef.current = null;
    setRangePreview(null);
    releaseRangePointerCapture(drag);
  }, []);

  useEffect(() => {
	pendingSectionTapRef.current = null;
    if (rangeToolEnabled) return;
    releaseRangePointerCapture(rangeDragRef.current);
    rangeDragRef.current = null;
    setRangePreview(null);
  }, [rangeToolEnabled]);

  useEffect(() => () => {
	pendingSectionTapRef.current = null;
    releaseRangePointerCapture(rangeDragRef.current);
    rangeDragRef.current = null;
  }, []);

  useEffect(() => {
    if (!rangeToolEnabled) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (document.getElementById(clipId)?.contains(target)) return;
      if (document.querySelector('.context-menu__content')?.contains(target)) return;
      ApplicationStore.setSelectedScriptLayerRange(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [clipId, rangeToolEnabled]);

  return (
    <Clip
      clipId={clipId}
      layerId={layerId}
      filename={filename}
      backgroundColor={"200, 213, 227"}
      isScriptLayerClip={true}
    >
      <div
        ref={clipContainerRef}
        className={`script-clip__interaction-layer${rangeToolEnabled ? ' script-clip__interaction-layer--range' : ''}`}
        aria-label="Script timeline"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseDown={(event) => {
          if (event.button === 0) event.stopPropagation();
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
		onPointerUp={handlePointerUp}
        onPointerCancel={cancelRangeDrag}
	        onLostPointerCapture={cancelRangeDrag}
	      >
	        {hoverSections.map((section) => (
	          <div
	            key={`section-surface-${section.id}`}
	            aria-hidden="true"
	            className={`script-clip__section-surface script-clip__section-surface--${section.kind}`}
	            style={{
	              left: `${(section.start / effectiveDuration) * 100}%`,
	              width: `${((section.end - section.start) / effectiveDuration) * 100}%`,
	            }}
	          >
	            <span className="script-clip__section-label">{section.label}</span>
	          </div>
	        ))}

	        {sceneBoundaryTransitions.map((boundary) => (
	          <div
	            key={`scene-divider-${boundary.boundarySceneIndex}`}
	            aria-hidden="true"
	            className="script-clip__section-divider script-clip__section-divider--scene"
	            style={{ left: `${boundary.position * 100}%` }}
	          />
	        ))}

	        {nonSceneSplitBoundaries.map((time, boundaryIndex) => (
	          <div
	            key={`edit-divider-${boundaryIndex}-${time.toFixed(3)}`}
	            aria-hidden="true"
	            className="script-clip__section-divider script-clip__section-divider--cut"
	            style={{ left: `${(time / effectiveDuration) * 100}%` }}
	          />
	        ))}

	        {gapInsertionSections.flatMap((gap, gapIndex) => [gap.start, gap.end].map((time, edgeIndex) => {
	          if (time <= 1e-3 || effectiveDuration - time <= 1e-3) return null;
	          return (
	            <div
	              key={`gap-divider-${gapIndex}-${edgeIndex}`}
	              aria-hidden="true"
	              className="script-clip__section-divider script-clip__section-divider--gap"
	              style={{ left: `${(time / effectiveDuration) * 100}%` }}
	            />
	          );
	        }))}

	        {selectedSceneSections.map((section) => (
	          <ScriptClipSectionSelection
	            key={`selected-${section.id}`}
	            duration={effectiveDuration}
	            range={section}
	            variant="scene"
	          />
	        ))}

	        <ScriptClipSectionSelection
	          duration={effectiveDuration}
	          range={activeSelectionChrome === 'transcript' ? transcriptHighlightRange : null}
	          variant="transcript"
	        />

	        <ScriptClipSectionSelection
	          duration={effectiveDuration}
	          range={activeSelectionChrome === 'tool' ? toolHighlightRange : null}
	          variant="tool"
	          interactive={rangeToolEnabled}
	        />

		        {isHovered && !rangeToolEnabled && (
		          <>
	            {hoverSections.length > 1 && hoveredSceneIndex !== null ? (
	              <div
	                className={`script-clip__section-hover script-clip__section-hover--${hoverSections[hoveredSceneIndex].kind}`}
	                style={{
	                  left: `${(hoverSections[hoveredSceneIndex].start / effectiveDuration) * 100}%`,
	                  width: `${((hoverSections[hoveredSceneIndex].end - hoverSections[hoveredSceneIndex].start) / effectiveDuration) * 100}%`,
	                }}
	              />
            ) : (
              <div className="script-clip__section-hover" />
            )}
		          </>
		        )}

			        {!rangeToolEnabled && isHovered && hoveredSceneIndex !== null && sceneBoundaryTransitions.map((boundary) => {
			          const showRightEdge = hoveredContentSceneIndex === boundary.prevSceneIndex;
			          const showLeftEdge = hoveredContentSceneIndex === boundary.boundarySceneIndex;
		          if (!showRightEdge && !showLeftEdge) return null;
		          const pct = boundary.position * 100;

          return (
            <React.Fragment key={`scene-boundary-ui-${boundary.boundarySceneIndex}`}>
              {/* Hovered scene's RIGHT edge (boundary is at the scene's right border) */}
              {showRightEdge && (
                <div
                  className="script-clip__boundary-hitbox script-clip__boundary-hitbox--out"
                  style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: `calc(${pct}% - 12px)`,
                    width: '12px',
                    pointerEvents: 'none',
                    zIndex: 15,
                  }}
                >
                  <div
                    data-script-clip-control="true"
                    style={{
                      position: 'relative',
                      width: '100%',
                      height: '100%',
                      pointerEvents: 'auto',
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <TransitionHandle
                      side="out"
                      currentConfig={toUIAudioConfig(boundary.outTransition)}
                      hasAdjacentClip={true}
                      onConfigChange={(config) => handleOutTransitionChange(
                        boundary.prevSceneIndex,
                        config,
                        undefined
                      )}
                      showVisual={true}
                      visualConfig={toUIVisualConfig(boundary.outTransition)}
                      onVisualConfigChange={(config) => handleOutTransitionChange(
                        boundary.prevSceneIndex,
                        undefined,
                        config
                      )}
                      offsetPercent={50}
                    />
                  </div>
                </div>
              )}

              {/* Hovered scene's LEFT edge (boundary is at the scene's left border) */}
              {showLeftEdge && (
                <div
                  className="script-clip__boundary-hitbox script-clip__boundary-hitbox--in"
                  style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: `${pct}%`,
                    width: '12px',
                    pointerEvents: 'none',
                    zIndex: 15,
                  }}
                >
                  <div
                    data-script-clip-control="true"
                    style={{
                      position: 'relative',
                      width: '100%',
                      height: '100%',
                      pointerEvents: 'auto',
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <TransitionHandle
                      side="in"
                      currentConfig={toUIAudioConfig(boundary.inTransition)}
                      hasAdjacentClip={true}
                      onConfigChange={(config) => handleInTransitionChange(
                        boundary.boundarySceneIndex,
                        config,
                        undefined
                      )}
                      showVisual={true}
                      visualConfig={toUIVisualConfig(boundary.inTransition)}
                      onVisualConfigChange={(config) => handleInTransitionChange(
                        boundary.boundarySceneIndex,
                        undefined,
                        config
                      )}
                      offsetPercent={50}
                    />
                  </div>
                </div>
              )}
            </React.Fragment>
          );
	        })}

	        {/* Scene transition fade overlays (triangular shading) */}
	        {sceneBoundaryTransitions.map((boundary) => {
	          const outVideoDurationSec = (boundary.outTransition?.video?.durationMs ?? 0) / 1000;
	          const inVideoDurationSec = (boundary.inTransition?.video?.durationMs ?? 0) / 1000;
          const outAudioDurationSec = (boundary.outTransition?.audio?.durationMs ?? 0) / 1000;
          const inAudioDurationSec = (boundary.inTransition?.audio?.durationMs ?? 0) / 1000;

          if (effectiveDuration <= 0) return null;

          const videoFadeOutWidth = outVideoDurationSec > 0
            ? Math.min(boundary.position, outVideoDurationSec / effectiveDuration)
            : 0;
          const videoFadeInWidth = inVideoDurationSec > 0
            ? Math.min(1 - boundary.position, inVideoDurationSec / effectiveDuration)
            : 0;

          const audioFadeOutWidth = outAudioDurationSec > 0
            ? Math.min(boundary.position, outAudioDurationSec / effectiveDuration)
            : 0;
          const audioFadeInWidth = inAudioDurationSec > 0
            ? Math.min(1 - boundary.position, inAudioDurationSec / effectiveDuration)
            : 0;

          if (
            videoFadeOutWidth <= 0 &&
            videoFadeInWidth <= 0 &&
            audioFadeOutWidth <= 0 &&
            audioFadeInWidth <= 0
          ) return null;

          const splitOut = videoFadeOutWidth > 0 && audioFadeOutWidth > 0;
          const splitIn = videoFadeInWidth > 0 && audioFadeInWidth > 0;

          return (
            <React.Fragment key={`transition-fade-${boundary.boundarySceneIndex}`}>
              {/* VIDEO fade-out overlay (before transition point) */}
              {videoFadeOutWidth > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    bottom: splitOut ? '50%' : 0,
                    right: `${(1 - boundary.position) * 100}%`,
                    width: `${videoFadeOutWidth * 100}%`,
                    pointerEvents: 'none',
                    zIndex: 5,
                  }}
                >
                  <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
                    <polygon points="0,0 100,0 100,100" fill="rgba(0,0,0,0.18)" />
                    <line x1="100" y1="100" x2="0" y2="0" stroke="rgba(255,255,255,0.55)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
                  </svg>
                </div>
              )}

              {/* AUDIO fade-out overlay (before transition point) */}
              {audioFadeOutWidth > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: splitOut ? '50%' : 0,
                    bottom: 0,
                    right: `${(1 - boundary.position) * 100}%`,
                    width: `${audioFadeOutWidth * 100}%`,
                    pointerEvents: 'none',
                    zIndex: 5,
                  }}
                >
                  <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
                    <polygon points="0,0 100,0 100,100" fill="rgba(0,0,0,0.30)" />
                    <line x1="100" y1="100" x2="0" y2="0" stroke="rgba(255,255,255,0.70)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
                  </svg>
                </div>
              )}

              {/* VIDEO fade-in overlay (after transition point) */}
              {videoFadeInWidth > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    bottom: splitIn ? '50%' : 0,
                    left: `${boundary.position * 100}%`,
                    width: `${videoFadeInWidth * 100}%`,
                    pointerEvents: 'none',
                    zIndex: 5,
                  }}
                >
                  <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
                    <polygon points="0,0 100,0 0,100" fill="rgba(0,0,0,0.18)" />
                    <line x1="0" y1="100" x2="100" y2="0" stroke="rgba(255,255,255,0.55)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
                  </svg>
                </div>
              )}

              {/* AUDIO fade-in overlay (after transition point) */}
              {audioFadeInWidth > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: splitIn ? '50%' : 0,
                    bottom: 0,
                    left: `${boundary.position * 100}%`,
                    width: `${audioFadeInWidth * 100}%`,
                    pointerEvents: 'none',
                    zIndex: 5,
                  }}
                >
                  <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
                    <polygon points="0,0 100,0 0,100" fill="rgba(0,0,0,0.30)" />
                    <line x1="0" y1="100" x2="100" y2="0" stroke="rgba(255,255,255,0.70)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
                  </svg>
                </div>
              )}
            </React.Fragment>
          );
        })}

      </div>

	      {/* Single unified waveform display */}
	      {finalPeaks && effectiveDuration > 0 && (
	        <div className="script-clip__waveform-viewport">
	          <ScriptWaveformDisplay
	            clipId={clipId}
	            peaks={finalPeaks}
                peaksRevision={displayPeaksVersion}
	            duration={effectiveDuration}
            gain={clipGain}
            scenes={scenes}
            speakerColorSegments={ScriptLayerStore.speakerWaveformSegments}
            speakerColorSegmentsRevision={ScriptLayerStore.speakerWaveformSegmentsVersion}
          />
        </div>
      )}

      {/* Processing indicator badge when waveform not ready */}
      {isProcessing && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          padding: '2px 6px',
          backgroundColor: 'rgba(55, 65, 81, 0.85)',
          borderRadius: '4px',
          pointerEvents: 'none',
          zIndex: 110,
        }}>
          <div style={{
            width: '10px',
            height: '10px',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            borderTopColor: '#fff',
            borderRadius: '50%',
            animation: 'clip-spin 1s linear infinite',
          }} />
          <span style={{
            color: '#fff',
            fontSize: '10px',
            fontWeight: 500,
          }}>Processing...</span>
        </div>
      )}
      <style>{`
        @keyframes clip-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Clip>
  );
});

export default ScriptClip;
